import { useMemo, useState } from 'react';
import {
  Drawer, Box, Stack, Typography, IconButton, Button, MenuItem, TextField, Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useDispatch, useSelector } from 'react-redux';
import {
  setViewerOpen, setViewerGroup, setViewerSort, setViewerFilter, clearViewerFilters,
} from '../../../../redux/tatWizardSlice';
import { buildViewerRows } from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';

const COLS = [
  { key: 'account_code', label: 'Account Code' },
  { key: 'parent', label: 'Parent' },
  { key: 'st_name', label: 'Service Type' },
  { key: 'service_type', label: 'ST Code' },
  { key: 'zone_name', label: 'Zone' },
  { key: 'tat_min', label: 'Min (d)' },
  { key: 'tat_max', label: 'Max (d)' },
  { key: 'payment', label: 'Payment' },
  { key: 'mode', label: 'Mode' },
];

function downloadCsv(name, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function CpCodeViewerDrawer() {
  const dispatch = useDispatch();
  const { open, group, sortKey, sortDir, filters } = useSelector((s) => s.tatWizard.viewer);
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const modeByST = useSelector((s) => s.tatWizard.modeByST);
  const merchantName = useSelector((s) => s.tatWizard.merchantName);
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs);
  const { data: serviceabilityGroups = [] } = useGetServiceabilityGroupsQuery();
  const [localFilters, setLocalFilters] = useState({});

  const allRows = useMemo(
    () => buildViewerRows(tat, tatBlended, modeByST, selectedSTs, serviceabilityGroups),
    [tat, tatBlended, modeByST, selectedSTs, serviceabilityGroups],
  );

  const filtered = useMemo(() => {
    const fx = { ...filters, ...localFilters };
    return allRows.filter((row) => {
      return Object.entries(fx).every(([k, v]) => {
        if (!v) return true;
        return String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase());
      });
    });
  }, [allRows, filters, localFilters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const an = Number(av);
      const bn = Number(bv);
      const numeric = !Number.isNaN(an) && !Number.isNaN(bn);
      const cmp = numeric ? an - bn : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const groups = useMemo(() => {
    if (group === 'none') return [['All TATs', sorted]];
    const map = new Map();
    sorted.forEach((r) => {
      const k = group === 'service_type' ? `${r.st_name} · ${r.service_type}` : r.parent;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return Array.from(map.entries());
  }, [sorted, group]);

  const onExport = () => {
    const header = COLS.map((c) => c.label).join(',');
    const lines = allRows.map((r) => COLS.map((c) => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(','));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`TAT_cp_code_viewer_${merchantName}_${stamp}.csv`, [header, ...lines].join('\n'));
  };

  const sortChip = (k) => {
    if (sortKey !== k) return null;
    return sortDir === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 12, ml: 0.5 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 12, ml: 0.5 }} />;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => dispatch(setViewerOpen(false))}
      PaperProps={{ sx: { width: { xs: '100vw', md: 1100 }, maxWidth: '95vw' } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 2.25, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>
          CP × Code TAT Viewer
        </Typography>
        <Chip size="small" label={`${sorted.length} of ${allRows.length} rows`} sx={{ mr: 1.25, fontWeight: 600 }} />
        <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} onClick={onExport}>
          Export CSV
        </Button>
        <IconButton size="small" onClick={() => dispatch(setViewerOpen(false))} sx={{ ml: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack
        direction="row"
        spacing={1.25}
        sx={{ px: 2.25, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}
      >
        <TextField
          select size="small" label="Group by"
          value={group}
          onChange={(e) => dispatch(setViewerGroup(e.target.value))}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="service_type">Service Type</MenuItem>
          <MenuItem value="parent">Parent</MenuItem>
          <MenuItem value="none">None</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Sort by"
          value={sortKey}
          onChange={(e) => dispatch(setViewerSort({ key: e.target.value }))}
          sx={{ minWidth: 160 }}
        >
          {COLS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Direction"
          value={sortDir}
          onChange={(e) => dispatch(setViewerSort({ dir: e.target.value }))}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="asc">Ascending</MenuItem>
          <MenuItem value="desc">Descending</MenuItem>
        </TextField>
        <Button
          size="small" variant="text" color="inherit"
          onClick={() => { setLocalFilters({}); dispatch(clearViewerFilters()); }}
        >
          Clear filters
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
          Filter rows live via the inputs in each column header
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.25, py: 1.5 }}>
        {groups.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>No rows match.</Typography>
        )}
        {groups.map(([gName, rows]) => (
          <Box key={gName} sx={{ mb: 2.5 }}>
            {group !== 'none' && (
              <Typography variant="subtitle2" sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>
                {gName} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>· {rows.length} row(s)</Box>
              </Typography>
            )}
            <Table size="small" sx={{ '& th, & td': { fontSize: 11.5 } }}>
              <TableHead>
                <TableRow>
                  {COLS.map((c) => (
                    <TableCell
                      key={c.key}
                      sx={{ fontWeight: 700, cursor: 'pointer', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}
                      onClick={() => {
                        if (sortKey === c.key) {
                          dispatch(setViewerSort({ dir: sortDir === 'asc' ? 'desc' : 'asc' }));
                        } else {
                          dispatch(setViewerSort({ key: c.key, dir: 'asc' }));
                        }
                      }}
                    >
                      <Stack direction="row" alignItems="center">{c.label}{sortChip(c.key)}</Stack>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {COLS.map((c) => (
                    <TableCell key={`${c.key}-f`} sx={{ p: 0.5, bgcolor: 'grey.50' }}>
                      <TextField
                        variant="standard"
                        placeholder="Filter…"
                        value={localFilters[c.key] ?? filters[c.key] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLocalFilters((s) => ({ ...s, [c.key]: v }));
                          dispatch(setViewerFilter({ col: c.key, value: v }));
                        }}
                        InputProps={{ disableUnderline: true, sx: { fontSize: 11 } }}
                        sx={{ width: '100%', px: 0.5 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.account_code}__${r.zone}`} hover>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{r.account_code}</TableCell>
                    <TableCell>{r.parent}</TableCell>
                    <TableCell>{r.st_name}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 10.5 }}>{r.service_type}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.zone_name}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.tat_min}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.tat_max}</TableCell>
                    <TableCell>{r.payment}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.mode === 'blended' ? 'Blended' : 'CP-wise'}
                        sx={{
                          height: 18, fontSize: 10,
                          bgcolor: r.mode === 'blended' ? '#fff7ed' : '#eef2ff',
                          color: r.mode === 'blended' ? '#9a3412' : '#3730a3',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}
